import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DollarSign, CreditCard, Smartphone, Wallet, Users, ArrowRight, Check, X, Banknote, Loader2
} from 'lucide-react';

const paymentMethods = [
  { id: 'cash', label: 'Cash', icon: Banknote, color: 'bg-emerald-500' },
  { id: 'online', label: 'Online', icon: Smartphone, color: 'bg-blue-500' },
  { id: 'credit', label: 'Credit', icon: Users, color: 'bg-red-500' },
];

export default function PaymentPanel({
  totalAmount = 0,
  onConfirm,
  onCancel,
  loading = false,
  customer,
}) {
  const [method, setMethod] = useState('cash');
  const [amountTendered, setAmountTendered] = useState('');
  const [referenceNo, setReferenceNo] = useState('');
  const [splitCash, setSplitCash] = useState('');
  const [splitOnline, setSplitOnline] = useState('');

  const change = useMemo(() => {
    if (method !== 'cash') return 0;
    const tendered = parseFloat(amountTendered) || totalAmount;
    return Math.max(0, tendered - totalAmount);
  }, [method, amountTendered, totalAmount]);

  const isPartialPayment = method === 'split';

  const handleConfirm = () => {
    const paymentData = {
      method,
      totalAmount,
    };

    if (method === 'cash') {
      paymentData.amountTendered = parseFloat(amountTendered) || totalAmount;
      paymentData.change = Math.max(0, paymentData.amountTendered - totalAmount);
    } else if (method === 'credit') {
      if (!customer) return;
      paymentData.customerId = customer.id || customer._id;
      paymentData.customerName = customer.fullName || customer.name;
    } else if (method === 'split') {
      paymentData.cashAmount = parseFloat(splitCash) || 0;
      paymentData.onlineAmount = parseFloat(splitOnline) || 0;
    } else {
      paymentData.referenceNo = referenceNo;
    }

    onConfirm?.(paymentData);
  };

  const canConfirm = useMemo(() => {
    if (loading) return false;
    if (totalAmount <= 0) return false;
    if (method === 'cash') {
      if (amountTendered === '') return true;
      return parseFloat(amountTendered) >= totalAmount;
    }
    if (method === 'credit') {
      return !!customer;
    }
    if (method === 'split') {
      const cash = parseFloat(splitCash) || 0;
      const online = parseFloat(splitOnline) || 0;
      return cash + online >= totalAmount && cash > 0 && online > 0;
    }
    return true;
  }, [loading, totalAmount, method, amountTendered, customer, splitCash, splitOnline]);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-base font-semibold text-gray-900 dark:text-white">Payment</h3>
        <button
          onClick={onCancel}
          className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <X size={18} />
        </button>
      </div>

      <div className="mb-6">
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Total Due</p>
        <p className="text-3xl font-bold text-gray-900 dark:text-white">Rs. {totalAmount.toLocaleString()}</p>
      </div>

      <div className="grid grid-cols-5 gap-2 mb-6">
        {paymentMethods.map((pm) => {
          const selected = method === pm.id;
          return (
            <motion.button
              key={pm.id}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => {
                setMethod(pm.id);
                if (pm.id !== 'cash') setAmountTendered('');
                if (pm.id !== 'credit') setReferenceNo('');
              }}
              className={`flex flex-col items-center gap-1.5 py-3 px-1 rounded-xl transition-all duration-200 border-2 ${
                selected
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              <pm.icon size={20} className={selected ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500'} />
              <span className={`text-[10px] font-medium ${selected ? 'text-primary-700 dark:text-primary-400' : 'text-gray-500 dark:text-gray-400'}`}>
                {pm.label}
              </span>
            </motion.button>
          );
        })}
      </div>

      <AnimatePresence mode="wait">
        {method === 'cash' && (
          <motion.div
            key="cash"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-3"
          >
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Amount Tendered</label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <DollarSign size={16} />
                </div>
                <input
                  type="number"
                  value={amountTendered}
                  onChange={(e) => setAmountTendered(e.target.value)}
                  placeholder="Enter amount"
                  className="w-full pl-9 pr-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500 placeholder-gray-400"
                  autoFocus
                />
              </div>
            </div>
            {change > 0 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex items-center justify-between p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg"
              >
                <span className="text-sm font-medium text-emerald-700 dark:text-emerald-400">Change Due</span>
                <span className="text-lg font-bold text-emerald-700 dark:text-emerald-400">Rs. {change.toLocaleString()}</span>
              </motion.div>
            )}
            {parseFloat(amountTendered) > 0 && parseFloat(amountTendered) < totalAmount && (
              <p className="text-xs text-amber-500 flex items-center gap-1">
                <DollarSign size={12} /> Insufficient amount. Need Rs. {(totalAmount - parseFloat(amountTendered)).toLocaleString()} more
              </p>
            )}
          </motion.div>
        )}

        {method === 'credit' && (
          <motion.div
            key="credit"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-3"
          >
            {customer ? (
              <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-600 dark:text-primary-400 font-semibold text-sm">
                  {(customer.fullName || customer.name || '?').charAt(0).toUpperCase()}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{customer.fullName || customer.name}</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500">{customer.phoneNumber || customer.phone}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-400 dark:text-gray-500">Pending</p>
                  <p className="text-sm font-semibold text-amber-600 dark:text-amber-400">
                    Rs. {(customer.currentCredit || 0).toLocaleString()}
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-amber-500 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg">
                Please select a customer for credit sale
              </p>
            )}
          </motion.div>
        )}

        {method === 'split' && (
          <motion.div
            key="split"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-3"
          >
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Cash Amount</label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <Banknote size={16} />
                </div>
                <input
                  type="number"
                  value={splitCash}
                  onChange={(e) => setSplitCash(e.target.value)}
                  placeholder="Cash amount"
                  className="w-full pl-9 pr-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500 placeholder-gray-400"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Online Amount</label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <Smartphone size={16} />
                </div>
                <input
                  type="number"
                  value={splitOnline}
                  onChange={(e) => setSplitOnline(e.target.value)}
                  placeholder="Online amount"
                  className="w-full pl-9 pr-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500 placeholder-gray-400"
                />
              </div>
            </div>
            {(parseFloat(splitCash) + parseFloat(splitOnline)) > 0 && (
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <span className="text-sm text-gray-500 dark:text-gray-400">Total</span>
                <span className={`text-sm font-bold ${(parseFloat(splitCash) + parseFloat(splitOnline)) >= totalAmount ? 'text-emerald-600' : 'text-red-500'}`}>
                  Rs. {(parseFloat(splitCash) + parseFloat(splitOnline)).toLocaleString()}
                  {(parseFloat(splitCash) + parseFloat(splitOnline)) >= totalAmount ? (
                    <Check size={14} className="inline ml-1" />
                  ) : null}
                </span>
              </div>
            )}
          </motion.div>
        )}

        {(method === 'online') && (
          <motion.div
            key={method}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-3"
          >
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">Reference Number</label>
              <input
                type="text"
                value={referenceNo}
                onChange={(e) => setReferenceNo(e.target.value)}
                placeholder="Enter transaction reference"
                className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:border-primary-500 placeholder-gray-400"
                autoFocus
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="mt-6 space-y-3">
        <motion.button
          whileHover={canConfirm ? { scale: 1.01 } : {}}
          whileTap={canConfirm ? { scale: 0.99 } : {}}
          onClick={handleConfirm}
          disabled={!canConfirm}
          className="w-full py-3 text-sm font-semibold rounded-xl transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed bg-primary-600 hover:bg-primary-700 text-white shadow-sm"
        >
          {loading ? (
            <Loader2 size={18} className="animate-spin" />
          ) : (
            <>
              <Check size={18} />
              Confirm Payment
            </>
          )}
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
          onClick={onCancel}
          disabled={loading}
          className="w-full py-2.5 text-sm font-medium rounded-xl transition-all duration-200 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
        >
          Cancel
        </motion.button>
      </div>
    </div>
  );
}
